import React, { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  generatePath,
  Link,
  matchPath,
  Navigate,
  Outlet,
  PathMatch
} from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { RouteMeta, Routes } from './routes'
import { Roles } from '@/core/enums/roles'
import { TipoUsuario } from '@/core/enums/tipo-usuario'
import { getLabel } from '@/core/utils/global-labels.js'
import { AccessControl } from '@/core/utils/access-control'
import {
  OpsErrorBoundary,
  OpsErrorMessage
} from '@/core/components/ops-error-boundary'
import { PageNotFound } from '@/core/components/page-not-found'
import { Loading } from '@/core/components/loading'
import App from '@/App'
import { HomeDesktop } from '@/modules/home/ui/home-desktop'
import { SuperAdmin } from '@/modules/super-admin/ui/super-admin'
import { ImportarUsuarios } from '@/modules/importar-usuarios/ui/importar-usuarios'
import BibliotecaVirtual from '@/modules/biblioteca-virtual/ui/biblioteca-virtual'
import UsuarioEdicaoWizard from '@/modules/usuarios-edicao/ui/usuario-edicao-wizard'

const Auditoria = lazy(() => import('@/modules/auditoria/ui/auditoria'))
const MinhaContaContainer = lazy(
  () => import('@/modules/minha-conta/ui/minha-conta')
)
const DealerListagem = lazy(
  () => import('@/modules/dealer-listagem/ui/dealer-listagem')
)
const DealerEdicaoContainer = lazy(
  () => import('@/modules/dealer-edicao/ui/dealer-wizard')
)
const GrupoListagemContainer = lazy(
  () => import('@/modules/grupo-listagem/ui/grupo-dealers-listagem')
)
const GrupoUsuariosListagemContainerCortex = lazy(
  () => import('@/modules/grupo-usuarios-listagem/ui/grupo-usuarios-listagem')
)
const GrupoUsuariosEdicaoContainer = lazy(
  () => import('@/modules/grupo-usuarios-edicao/ui/grupo-usuarios-edicao')
)
const UsuarioListagemContainer = lazy(
  () => import('@/modules/usuarios-listagem/ui/usuario-listagem')
)
const AusenciaTemporariaListagem = lazy(
  () => import('@/modules/ausencia-temporaria/ui/ausencia-temporaria')
)
const ComunicadoListagem = lazy(
  () => import('@/modules/comunicado-listagem/ui/comunicado-listagem-desktop')
)
const ComunicadoDashboardContainer = lazy(
  () => import('@/modules/comunicado-dashboard/ui/comunicado-dashboard')
)
const ComunicadoVisualizacaoContainer = lazy(
  () => import('@/modules/comunicado-visualizacao/ui/comunicado-visualizacao')
)
const ComunicadoEnvioVisualizacao = lazy(
  () => import('@/modules/comunicado-visualizacao/ui/comunicado-envio')
)
const ComunicadoEdicaoContainer = lazy(
  () => import('@/modules/comunicado-edicao/ui/comunicado-edicao')
)
const SlideListagem = lazy(
  () => import('@/modules/slides-listagem/ui/slide-listagem')
)
const SlideEdicaoContainer = lazy(
  () => import('@/modules/slides-edicao/ui/slide-edicao')
)
const AnuncioListagem = lazy(
  () => import('@/modules/anuncio-listagem/ui/anuncio-listagem')
)
const AnuncioEdicaoContainer = lazy(
  () => import('@/modules/anuncio-edicao/ui/anuncio-edicao')
)
const NotificacaoListagemContainer = lazy(
  () => import('@/modules/notificacoes/ui/notificacao-listagem')
)
const NotificacaoGenericaListagemContainer = lazy(
  () =>
    import(
      '@/modules/notificacoes-genericas-listagem/ui/notificacao-generica-listagem'
    )
)
const NotificacaoGenericaEdicaoContainer = lazy(
  () =>
    import(
      '@/modules/notificacoes-genericas-edicao/ui/notificacao-generica-edicao'
    )
)
const GrupoEdicaoContainer = lazy(
  () => import('@/modules/grupo-edicao/ui/grupo-edicao')
)
const RelatorioPage = lazy(() => import('@/modules/relatorios/ui/index'))

const DocumentTitle = ({
  title,
  children
}: {
  title?: string | (() => string | undefined) | undefined
  children: React.ReactNode
}) => {
  if (title) {
    const currentTitle = document.title
    const split = currentTitle.split('-')
    const titleAux = typeof title === 'function' ? title() : title

    if (titleAux) {
      document.title = split[0].trim().concat(' - ').concat(titleAux)
    }
  }
  return <>{children}</>
}

const routeElement = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
  title?: string | (() => string | undefined),
  roles?: Roles[],
  tiposUsuario?: TipoUsuario[]
) => (
  <OpsErrorBoundary>
    <AccessControl
      allowedRoles={roles}
      allowedUserTypes={tiposUsuario}
      renderNoAccess={<Navigate to={Routes.HOME} />}
    >
      <DocumentTitle title={title}>
        <Suspense fallback={<Loading />}>
          <Component />
        </Suspense>
      </DocumentTitle>
    </AccessControl>
  </OpsErrorBoundary>
)

const routeLabelByPath = new Map(RouteMeta.map(m => [m.path, m.label]))

const customBreadcrumbs = (path: string, match: PathMatch<string> | null) => {
  if (path === Routes.NOTIFICACAO_NOVA && match?.params.tipo === 'modelo') {
    return {
      title: t`Novo Modelo de Notificação`,
      Component: 'span',
      componentProps: {}
    }
  }
  if (path === Routes.NOTIFICACAO_EDICAO && match?.params.tipo === 'modelo') {
    return {
      title: t`Editar Modelo de Notificação`,
      Component: 'span',
      componentProps: {}
    }
  }
  if (
    path === Routes.NOTIFICACAO_EDICAO &&
    match?.params.tipo === 'comModelo'
  ) {
    return {
      title: t`Criar Notificação com Modelo`,
      Component: 'span',
      componentProps: {}
    }
  }
  return null
}

const breadcrumb = (path: string) => {
  const title = routeLabelByPath.get(path) ?? ''

  const isExactMatch = Boolean(
    matchPath({ path, end: true }, location.pathname)
  )
  const match = matchPath({ path, end: false }, location.pathname)

  const customBreadcrumb = customBreadcrumbs(path, match)
  if (customBreadcrumb !== null) {
    return customBreadcrumb
  }

  if (isExactMatch) {
    return {
      title,
      Component: 'span',
      componentProps: {}
    }
  }

  const to =
    match && path.includes(':') ? generatePath(path, match.params) : path

  return {
    title,
    Component: Link,
    componentProps: {
      to,
      className: 'no-underline'
    }
  }
}

export const router = createBrowserRouter([
  {
    path: Routes.ROOT,
    element: <App />,
    errorElement: (
      <OpsErrorBoundary>
        <OpsErrorMessage />
      </OpsErrorBoundary>
    ),
    handle: {
      crumb: () => {
        if (location.pathname === Routes.HOME) {
          return {
            title: t`Portal`,
            Component: 'span',
            componentProps: {}
          }
        }
        return {
          title: t`Portal`,
          Component: Link,
          componentProps: {
            to: Routes.HOME,
            className: 'no-underline'
          }
        }
      }
    },
    children: [
      {
        index: true,
        element: <Navigate to={Routes.HOME} />
      },
      {
        path: Routes.HOME,
        element: routeElement(HomeDesktop, t`Página Inicial`),
        handle: {
          crumb: () => breadcrumb(Routes.HOME)
        }
      },
      {
        path: Routes.SUPER_ADMIN,
        element: routeElement(SuperAdmin, t`Super Admin`, undefined, [
          TipoUsuario.TECSINAPSE
        ]),
        handle: {
          crumb: () => breadcrumb(Routes.SUPER_ADMIN)
        }
      },
      {
        path: Routes.GRUPOS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.GRUPOS)
        },
        children: [
          {
            index: true,
            element: routeElement(
              GrupoListagemContainer,
              () => getLabel('label.grupos'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            )
          },
          {
            path: Routes.GRUPO_NOVO,
            element: routeElement(
              GrupoEdicaoContainer,
              () => getLabel('novo.grupo'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.GRUPO_NOVO)
            }
          },
          {
            path: Routes.GRUPO,
            element: routeElement(
              GrupoEdicaoContainer,
              () => getLabel('label.grupos'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.GRUPO)
            }
          }
        ]
      },
      {
        path: Routes.DEALERS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.DEALERS)
        },
        children: [
          {
            index: true,
            element: routeElement(
              DealerListagem,
              () => getLabel('label.dealers'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            )
          },
          {
            path: Routes.DEALER_NOVO,
            element: routeElement(
              DealerEdicaoContainer,
              () => getLabel('novo.dealer'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.DEALER_NOVO)
            }
          },
          {
            path: Routes.DEALER,
            element: routeElement(
              DealerEdicaoContainer,
              () => getLabel('label.dealer'),
              [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.DEALER)
            }
          }
        ]
      },
      {
        path: Routes.USUARIOS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.USUARIOS)
        },
        children: [
          {
            index: true,
            element: routeElement(UsuarioListagemContainer, t`Usuários`, [
              Roles.PORTAL_MANUTENCAO_USUARIOS,
              Roles.PORTAL_CONSULTA_VISUALIZACAO_USUARIOS
            ])
          },
          {
            path: Routes.USUARIO_NOVO,
            element: routeElement(UsuarioEdicaoWizard, t`Novo Usuário`, [
              Roles.PORTAL_MANUTENCAO_USUARIOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.USUARIO_NOVO)
            }
          },
          {
            path: Routes.USUARIO,
            element: routeElement(UsuarioEdicaoWizard, t`Usuário`, [
              Roles.PORTAL_MANUTENCAO_USUARIOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.USUARIO)
            }
          },
          {
            path: Routes.AUSENCIA_TEMPORARIA,
            element: routeElement(
              AusenciaTemporariaListagem,
              t`Ausências Temporárias`,
              [Roles.PORTAL_AUSENCIA_TEMPORARIA]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.AUSENCIA_TEMPORARIA)
            }
          }
        ]
      },
      {
        path: Routes.GRUPOS_USUARIOS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.GRUPOS_USUARIOS)
        },
        children: [
          {
            index: true,
            element: routeElement(
              GrupoUsuariosListagemContainerCortex,
              t`Grupos de Usuários`,
              [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS]
            )
          },
          {
            path: Routes.GRUPO_USUARIOS_NOVO,
            element: routeElement(
              GrupoUsuariosEdicaoContainer,
              t`Novo Grupo de Usuários`,
              [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.GRUPO_USUARIOS_NOVO)
            }
          },
          {
            path: Routes.GRUPO_USUARIOS,
            element: routeElement(
              GrupoUsuariosEdicaoContainer,
              t`Grupos de Usuários`,
              [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.GRUPO_USUARIOS)
            }
          }
        ]
      },
      {
        path: Routes.AUDITORIA,
        element: routeElement(Auditoria, t`Auditoria`, [
          Roles.PORTAL_MANUTENCAO_USUARIOS,
          Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO,
          Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS,
          Roles.PORTAL_MANUTENCAO_SLIDES,
          Roles.PORTAL_MANUTENCAO_ANUNCIOS
        ]),
        handle: {
          crumb: () => breadcrumb(Routes.AUDITORIA)
        }
      },
      {
        path: Routes.BIBLIOTECA_VIRTUAL,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.BIBLIOTECA_VIRTUAL)
        },
        children: [
          {
            index: true,
            element: routeElement(BibliotecaVirtual, t`Biblioteca Virtual`)
          },
          {
            path: Routes.BIBLIOTECA_VIRTUAL_PASTA,
            element: routeElement(BibliotecaVirtual, t`Biblioteca Virtual`),
            handle: {
              crumb: () => breadcrumb(Routes.BIBLIOTECA_VIRTUAL_PASTA)
            }
          }
        ]
      },
      {
        path: Routes.COMUNICADOS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.COMUNICADOS)
        },
        children: [
          {
            index: true,
            element: routeElement(ComunicadoListagem, t`Comunicados`)
          },
          {
            path: Routes.COMUNICADOS_NOVO,
            element: routeElement(
              ComunicadoEdicaoContainer,
              t`Novo Comunicado`,
              [
                Roles.PORTAL_CRIACAO_COMUNICADOS,
                Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
              ]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADOS_NOVO)
            }
          },
          {
            path: Routes.COMUNICADO_EDICAO,
            element: routeElement(ComunicadoEdicaoContainer, t`Comunicado`, [
              Roles.PORTAL_CRIACAO_COMUNICADOS,
              Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADO_EDICAO)
            }
          },
          {
            path: Routes.COMUNICADO_ERRATA,
            element: routeElement(ComunicadoEdicaoContainer, t`Comunicado`, [
              Roles.PORTAL_CRIACAO_COMUNICADOS,
              Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADO_ERRATA)
            }
          },
          {
            path: Routes.COMUNICADO_ENVIO_VISUALIZACAO,
            element: routeElement(ComunicadoEnvioVisualizacao, t`Comunicado`),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADO_ENVIO_VISUALIZACAO)
            }
          },
          {
            path: Routes.COMUNICADO_VISUALIZACAO,
            element: routeElement(
              ComunicadoVisualizacaoContainer,
              t`Comunicado`,
              [
                Roles.PORTAL_CRIACAO_COMUNICADOS,
                Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
              ]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADO_VISUALIZACAO)
            }
          },
          {
            path: Routes.COMUNICADO_DASHBOARD,
            element: routeElement(ComunicadoDashboardContainer, t`Comunicado`, [
              Roles.PORTAL_CRIACAO_COMUNICADOS,
              Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.COMUNICADO_DASHBOARD)
            }
          }
        ]
      },
      {
        path: Routes.MINHA_CONTA,
        element: routeElement(MinhaContaContainer, t`Minha Conta`),
        handle: {
          crumb: () => breadcrumb(Routes.MINHA_CONTA)
        }
      },
      {
        path: Routes.SLIDES,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.SLIDES)
        },
        children: [
          {
            index: true,
            element: routeElement(SlideListagem, t`Slides`, [
              Roles.PORTAL_MANUTENCAO_SLIDES
            ])
          },
          {
            path: Routes.SLIDE_NOVO,
            element: routeElement(SlideEdicaoContainer, t`Novo Slide`, [
              Roles.PORTAL_MANUTENCAO_SLIDES
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.SLIDE_NOVO)
            }
          },
          {
            path: Routes.SLIDE_EDICAO,
            element: routeElement(SlideEdicaoContainer, t`Slides`, [
              Roles.PORTAL_MANUTENCAO_SLIDES
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.SLIDE_EDICAO)
            }
          }
        ]
      },
      {
        path: Routes.ANUNCIOS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.ANUNCIOS)
        },
        children: [
          {
            index: true,
            element: routeElement(AnuncioListagem, t`Anúncios`, [
              Roles.PORTAL_MANUTENCAO_ANUNCIOS
            ])
          },
          {
            path: Routes.ANUNCIO_NOVO,
            element: routeElement(AnuncioEdicaoContainer, t`Novo Anúncio`, [
              Roles.PORTAL_MANUTENCAO_ANUNCIOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.ANUNCIO_NOVO)
            }
          },
          {
            path: Routes.ANUNCIO_EDICAO,
            element: routeElement(AnuncioEdicaoContainer, t`Anúncios`, [
              Roles.PORTAL_MANUTENCAO_ANUNCIOS
            ]),
            handle: {
              crumb: () => breadcrumb(Routes.ANUNCIO_EDICAO)
            }
          }
        ]
      },
      {
        path: Routes.NOTIFICACOES,
        element: routeElement(NotificacaoListagemContainer, t`Notificações`),
        handle: {
          crumb: () => breadcrumb(Routes.NOTIFICACOES)
        }
      },
      {
        path: Routes.NOTIFICACOES_GENERICAS,
        element: <Outlet />,
        handle: {
          crumb: () => breadcrumb(Routes.NOTIFICACOES_GENERICAS)
        },
        children: [
          {
            index: true,
            element: routeElement(
              NotificacaoGenericaListagemContainer,
              t`Notificações`,
              [Roles.PORTAL_NOTIFICACOES_GENERICAS]
            )
          },
          {
            path: Routes.NOTIFICACAO_NOVA,
            element: routeElement(
              NotificacaoGenericaEdicaoContainer,
              t`Notificações`,
              [Roles.PORTAL_NOTIFICACOES_GENERICAS]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.NOTIFICACAO_NOVA)
            }
          },
          {
            path: Routes.NOTIFICACAO_EDICAO,
            element: routeElement(
              NotificacaoGenericaEdicaoContainer,
              t`Notificações`,
              [Roles.PORTAL_NOTIFICACOES_GENERICAS]
            ),
            handle: {
              crumb: () => breadcrumb(Routes.NOTIFICACAO_EDICAO)
            }
          }
        ]
      },
      {
        path: Routes.IMPORTAR_USUARIOS,
        element: routeElement(ImportarUsuarios, t`Importar usuários`, [
          Roles.PORTAL_IMPORTACAO_USUARIOS
        ]),
        handle: {
          crumb: () => breadcrumb(Routes.IMPORTAR_USUARIOS)
        }
      },
      {
        path: Routes.RELATORIO_PAGE,
        element: routeElement(RelatorioPage, t`Relatório`),
        handle: {
          crumb: () => breadcrumb(Routes.RELATORIO_PAGE)
        }
      },
      {
        path: '*',
        element: <PageNotFound />
      }
    ]
  }
])
